import React, { createRef, RefObject, useEffect, useRef } from 'react'
import { parse } from 'url'
import { RouteComponentProps } from 'react-router-dom'
import styled from 'styled-components'

import { APP_VERSION } from '../../lib/query-parameters'
import { log, track } from '../../utils/log'
import { addContext } from '@acorns/admin-context'
import { AdminApp } from '@acorns/admin-framework-binding'
import { useAuthContext } from '../../contexts/auth'
import { PROXY_CONFIG } from '../../config/proxy.config'
import {
  trackAdminPortalMfeScreenViewedPayload,
  trackAdminPortalScreenViewedPayload,
} from '../../../generated/trackingEvents'

export type Props = {
  app: AdminApp
} & Partial<RouteComponentProps>

const Container = styled.div`
  max-height: 100vh;
`

const Frame = styled('iframe')`
  opacity: 1;
  transition: opacity 300ms;
`

const FramedRenderer = ({ app, history, location }: Props) => {
  const ref: RefObject<HTMLIFrameElement> = createRef()
  const { adminUser } = useAuthContext()
  const previousPathRef = useRef<string>('')

  const { pathname, search } = location

  useEffect(() => {
    shimHistory()
  }, [])

  useEffect(() => {
    if (adminUser && pathname && previousPathRef.current !== pathname) {
      const [eventName, payload] = trackAdminPortalScreenViewedPayload()
      track(eventName, payload, adminUser.id)
      previousPathRef.current = pathname
    }
  }, [pathname, adminUser, app])

  function resolveDynamicSource(originalSource: string): string {
    const { query } = parse(location.search, true)

    let source = originalSource

    //Handle APP_VERSION query parameter
    if (query?.[APP_VERSION]) {
      const segments = originalSource.split('/')
      source = `${segments.slice(0, segments.length - 1).join('/')}/${query[APP_VERSION]}`
    }

    const isLocalDevelopment = process.env.BUILD_ENV === 'local'

    if (isLocalDevelopment) {
      source = PROXY_CONFIG.transformUrlForDevelopment(source)
    }

    return source
  }

  const source = resolveDynamicSource(app.source)

  addContext({ app: app, resolvedRootPath: parse(source).pathname + app.rootPath })

  const src = `${source}${pathname}${search}`

  log(src)

  function shimHistory() {
    const iframe = ref.current

    if (!iframe) {
      return
    }

    const { pushState } = iframe.contentWindow.history

    iframe.contentWindow.history.pushState = (...args) => {
      if (Array.isArray(args)) {
        // Default behavior
        pushState.apply(iframe.contentWindow.history, args)

        const pathSegments = (args as Array<any>)[2].split(app.rootPath)
        const currentPath = pathSegments.pop()

        if (adminUser?.id) {
          const [eventName, payload] = trackAdminPortalMfeScreenViewedPayload({
            url: app.rootPath + currentPath,
          })
          track(eventName, payload, adminUser.id)
        }

        // Syncs parent window location with child
        history.replace(app.rootPath + currentPath)
      }
    }
  }

  return (
    <Container>
      <Frame data-testid="framed_renderer" id="framed_renderer" src={src} ref={ref} />
    </Container>
  )
}

export { FramedRenderer }
